import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

function Admin() {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    category: "",
    tags: "",
    image: "",
    author: "", 
  });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [categoryMap, setCategoryMap] = useState({});

  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  useEffect(() => {
    if (token) {
      fetchPosts();
      fetchCategories();
      fetchUsers(currentPage);
    } else {
      navigate("/login");
    }
  }, [token, currentPage, navigate]);

  const fetchUsers = async (page) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `https://blog-backend-omega-peach.vercel.app/api/users?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMakeAdmin = async (userId) => {
    if (window.confirm("Are you sure you want to make this user an admin?")) {
      try {
        await axios.put(
          `https://blog-backend-omega-peach.vercel.app/api/users/make-admin/${userId}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchUsers(currentPage);
      } catch (error) {
        console.error("Error making user admin:", error);
        alert("Failed to make user an admin. Please try again.");
      }
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(
          `https://blog-backend-omega-peach.vercel.app/api/users/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchUsers(currentPage);
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Failed to delete user. Please try again.");
      }
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await axios.delete(
          `https://blog-backend-omega-peach.vercel.app/api/categories/${categoryId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
        alert("Failed to delete category. Please try again.");
      }
    }
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  const fetchPosts = async () => {
    try {
      const response = await axios.get(
        "https://blog-backend-omega-peach.vercel.app/api/posts",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPosts(response.data);
      console.log("Token received:", response.data.token);
    } catch (error) {
      console.error("Error fetching posts:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        "https://blog-backend-omega-peach.vercel.app/api/categories",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategories(response.data);
      const map = {};
      response.data.forEach((cat) => (map[cat._id] = cat.name));
      setCategoryMap(map);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleInputChange = (e) => {
    setNewPost({ ...newPost, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const postData = {
        title: newPost.title,
        content: newPost.content,
        category: newPost.category,
        tags: newPost.tags.split(",").map((tag) => tag.trim()),
        image: newPost.image,
        // The author should be set on the server based on the authenticated user
      };

      const response = await axios.post(
        "https://blog-backend-omega-peach.vercel.app/api/posts",
        postData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Post created:", response.data);
      fetchPosts();
      setNewPost({
        title: "",
        content: "",
        category: "",
        tags: "",
        image: "",
      });
    } catch (error) {
      console.error(
        "Error creating post:",
        error.response ? error.response.data : error.message
      );
      alert("Failed to add post. Please try again.");
    }
  };
  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `https://blog-backend-omega-peach.vercel.app/api/posts/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchPosts();
    } catch (error) {
      alert("Failed to delete post. Please try again.");
    }
  };

  const handleCategoryChange = (e) => {
    setNewCategory(e.target.value);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        "blog-backend-omega-peach.vercel.app/api/categories",
        { name: newCategory },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchCategories();
      setNewCategory("");
    } catch (error) {
      alert("Failed to add category. Please try again.");
    }
  };

  if (!token) {
    return <div>Please log in to access the admin panel.</div>;
  }

  return (
    <div className="container mx-auto p-4 mt-20">
      <h2 className="text-2xl font-bold mb-4">Admin: Manage Blog Posts</h2>

      <form onSubmit={handleSubmit} className="mb-8">
        <input
          type="text"
          name="title"
          value={newPost.title}
          onChange={handleInputChange}
          placeholder="Post title"
          className="w-full p-2 mb-4 border rounded"
        />
        <textarea
          name="content"
          value={newPost.content}
          onChange={handleInputChange}
          placeholder="Post content"
          className="w-full p-2 mb-4 border rounded"
          rows="4"
        ></textarea>
        <select
          name="category"
          value={newPost.category}
          onChange={handleInputChange}
          className="w-full p-2 mb-4 border rounded"
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category._id} value={category._id}>
              {category.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          name="tags"
          value={newPost.tags}
          onChange={handleInputChange}
          placeholder="Tags (comma-separated)"
          className="w-full p-2 mb-4 border rounded"
        />
        <input
          type="text"
          name="image"
          value={newPost.image}
          onChange={handleInputChange}
          placeholder="Image URL"
          className="w-full p-2 mb-4 border rounded"
        />
        <button
          type="submit"
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
        >
          Add Post
        </button>
      </form>

      <h3 className="text-xl font-bold mb-2">Add New Category:</h3>
      <form onSubmit={handleCategorySubmit} className="mb-8">
        <input
          type="text"
          value={newCategory}
          onChange={handleCategoryChange}
          placeholder="New Category"
          className="w-full p-2 mb-4 border rounded"
        />
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Add Category
        </button>
      </form>

      <h3 className="text-xl font-bold mb-2">Manage Users:</h3>
      <div className="overflow-x-auto">
        <table className="w-full table-auto">
          <thead>
            <tr>
              <th className="px-4 py-2">Username</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id}>
                <td className="border px-4 py-2">{user.username}</td>
                <td className="border px-4 py-2">
                  {!user.isAdmin && (
                    <button
                      onClick={() => handleMakeAdmin(user._id)}
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded mr-2"
                    >
                      Make Admin
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteUser(user._id)}
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination controls */}
      <div className="flex justify-between mt-4">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <h3 className="text-xl font-bold mb-2">Your Posts:</h3>
      {posts.map((post) => (
        <div key={post._id} className="bg-white p-4 mb-4 rounded shadow">
          <h4 className="text-lg font-semibold">{post.title}</h4>
          <p className="text-gray-600">
            By {post.author} on {new Date(post.createdAt).toLocaleDateString()}{" "}
            in {categoryMap[post.category] || "Uncategorized"}
          </p>
          <p className="mb-2">{post.content.substring(0, 100)}...</p>
          <div className="mb-2">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="inline-block bg-gray-200 rounded-full px-3 py-1 text-sm font-semibold text-gray-700 mr-2"
              >
                #{tag}
              </span>
            ))}
          </div>
          <button
            onClick={() => handleDelete(post._id)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded"
          >
            Delete
          </button>
        </div>
      ))}
    </div>
  );
}

export default Admin;
